import { apiBase } from './apiBase';
import { ApiError } from './api';
import { useAuthStore } from '../store/useAuthStore';

// Frontend client for the Clients / Suppliers Excel export & import endpoints
// (backend CLE-229, wired by CLE-230). Export streams an .xlsx attachment;
// import is a two-step preview -> confirm flow that mirrors the backend's
// row-level validation envelope.

export type DirectoryEntity =
  | 'clients'
  | 'suppliers'
  | 'subscriptions'
  | 'tasks'
  | 'expense-categories';

// --- Shapes (mirror backend/src/lib/directory-excel) -----------------------

export type RowStatus = 'valid' | 'warning' | 'error';

export interface PreviewRow {
  /** 1-based row number as shown in Excel (header is row 1). */
  rowNumber: number;
  /** Parsed values keyed by model field (e.g. `name`, `cifCui`). */
  data: Record<string, unknown>;
  status: RowStatus;
  issues: string[];
}

export interface PreviewSummary {
  total: number;
  valid: number;
  warnings: number;
  errors: number;
}

/** A category code the file uses that the chosen firm does not have. */
export interface UnknownCategory {
  code: string;
  rows: number[];
}

export interface PreviewResult {
  rows: PreviewRow[];
  summary: PreviewSummary;
  /** Rows using these codes are errors until the categories are created. */
  unknownCategories?: UnknownCategory[];
}

export interface ConfirmResult {
  created: number;
  /** Rows that matched a previously deleted record: reactivated and updated. */
  revived: number;
  skipped: number;
  failed: { rowNumber: number; error: string }[];
  /** The import-history entry of this run. */
  batchId?: string;
}

export interface ConfirmRow {
  rowNumber: number;
  data: Record<string, unknown>;
}

interface Envelope<T> {
  success: true;
  data: T;
}

// --- Helpers ----------------------------------------------------------------

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function readEnvelopeError(parsed: unknown, fallback: string): { message: string; code?: string } {
  if (parsed && typeof parsed === 'object' && 'error' in parsed) {
    const err = (parsed as { error: unknown }).error;
    if (err && typeof err === 'object') {
      const e = err as { message?: string; code?: string };
      return { message: e.message ?? fallback, code: e.code };
    }
  }
  return { message: fallback };
}

// --- Export -----------------------------------------------------------------

/**
 * Download the entity's .xlsx export and trigger a browser save. The route
 * streams a binary attachment (and doubles as the import template), so this
 * bypasses apiFetch's JSON handling. The server sets the date-stamped
 * filename via Content-Disposition; we fall back to a sensible default.
 */
export async function downloadDirectoryExcel(
  entity: DirectoryEntity,
  companyId?: string | null,
): Promise<void> {
  const res = await fetch(`${apiBase()}/api/${entity}/export/excel${companyQuery(companyId)}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    let parsed: unknown;
    try {
      parsed = text ? JSON.parse(text) : undefined;
    } catch {
      parsed = undefined;
    }
    const { message, code } = readEnvelopeError(parsed, 'Could not export the file.');
    throw new ApiError(res.status, message, code);
  }

  const blob = await res.blob();
  const filename = filenameFromDisposition(res.headers.get('Content-Disposition'), entity);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function filenameFromDisposition(disposition: string | null, entity: DirectoryEntity): string {
  if (disposition) {
    const match = /filename="?([^"]+)"?/i.exec(disposition);
    if (match?.[1]) return match[1];
  }
  const date = new Date().toISOString().slice(0, 10);
  const base =
    entity === 'clients'
      ? 'clienti'
      : entity === 'suppliers'
        ? 'furnizori'
        : entity === 'subscriptions'
          ? 'abonamente'
          : entity === 'expense-categories'
            ? 'categorii'
            : 'task-uri';
  return `${base}-${date}.xlsx`;
}

function companyQuery(companyId: string | null | undefined): string {
  return companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
}

// --- Import: preview --------------------------------------------------------

/**
 * Upload a workbook for parse + validation (no DB write). Sends the raw file
 * body — the backend route uses a per-route raw parser that accepts any
 * content type. A 400 surfaces a workbook-level problem (empty / wrong headers
 * / oversize / non-xlsx) through the thrown ApiError's message + code.
 */
export async function previewDirectoryImport(
  entity: DirectoryEntity,
  file: File,
  /** Firm the rows go into (duplicates and category codes are checked there). */
  companyId?: string | null,
): Promise<PreviewResult> {
  const res = await fetch(`${apiBase()}/api/${entity}/import/excel${companyQuery(companyId)}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });
  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const { message, code } = readEnvelopeError(parsed, 'Could not read the file.');
    throw new ApiError(res.status, message, code);
  }
  return (parsed as Envelope<PreviewResult>).data;
}

// --- Import: confirm --------------------------------------------------------

/**
 * Create the reviewed rows in a transaction. Send only the rows the user kept
 * checked; the backend re-validates server-side and skips rows that collide
 * with an existing record, reporting per-row failures rather than aborting the
 * whole batch.
 */
export async function confirmDirectoryImport(
  entity: DirectoryEntity,
  rows: ConfirmRow[],
  companyId?: string | null,
  /** The uploaded file's name, recorded in the import history. */
  fileName?: string | null,
): Promise<ConfirmResult> {
  const res = await fetch(`${apiBase()}/api/${entity}/import/confirm`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: JSON.stringify({ rows, companyId: companyId ?? null, fileName: fileName ?? null }),
  });
  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const { message, code } = readEnvelopeError(parsed, 'Import failed.');
    throw new ApiError(res.status, message, code);
  }
  return (parsed as Envelope<ConfirmResult>).data;
}

// --- Display helpers --------------------------------------------------------

/**
 * Best-effort primary label for a parsed row, used in the preview/result
 * tables. Clients/Suppliers key the company/vendor name on `name`; Tasks key
 * it on `title`.
 */
export function rowLabel(row: { data: Record<string, unknown> }): string {
  const name = row.data.name ?? row.data.title ?? row.data.code;
  if (typeof name === 'string' && name.trim()) return name.trim();
  return '—';
}
